/*
 * Copyright (c) 2026. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* planitem.ts
* 2-11-2022 - Jelmer Jellema - Spin in het Web B.V.
*
* Model for planitem: one block in the planboard
*/

import {Basemodel, ModelField, ModelRelation} from './basemodel';
import {DateTime} from 'luxon';
import {PlanitemFunction} from './planitem_function';
import {Project} from './project';
import {Functie} from './functie';
import {PersoneelPlanitem} from './personeel_planitem';
import {Personeel} from './personeel';
import {hash} from '../app/types';
import {Leverancier} from './leverancier';
import {Planitemlog} from './planitemlog';

/**
 * Een probleem met de kwalificatie van een ingepland personeelslid op de dienstdatum: het
 * personeelslid mist een van de functies van de dienst, of zijn certificering is verlopen —
 * de algemene certificering (raamovereenkomst), of die voor een van de functies van de dienst.
 */
export type Kwalificatieprobleem = {
    ppi: PersoneelPlanitem;
    soort: 'functie' | 'certificering_algemeen' | 'certificering_functie';
    functie_id?: number; //bij certificering_functie: om welke functie het gaat
    einddatum?: string; //bij een certificering: de laatste dag waarop hij geldig was
};

/**
 * Eén regel kritiek, met de categorie waaronder hij valt en de inplanning waar hij over gaat. Die
 * laatste is nodig om regels weg te laten voor wie niet alle ingeplande mensen mag zien.
 */
type Kritiekregel = {
    cat: string;
    tekst: string;
    ppi?: PersoneelPlanitem;
};

export class Planitem extends Basemodel {
    static table = 'planitem';
    static override loglevel = 'debug';
    static override softdeletes = true;

    //owner is project:
    @ModelField() project_id: number;
    @ModelField() override id: number = null;
    @ModelField() capacity: number = 1;
    @ModelField() startdate: string = DateTime.now().toISODate();
    @ModelField() starttime: string = '07:00';
    @ModelField() endtime: string = '15:00';
    @ModelField() serieskey: string | null = null;
    @ModelField() opmerkingen: string = '';
    @ModelField() functionalias: string | null = null;
    @ModelField() aanvraagnummer: string | null = null;
    @ModelField() leverancier_id: number | null = null; //de leverancier die dit mag invullen
    @ModelField() leverancierlock: 'auto' | 'open' | 'dicht' = 'auto'; //mag leverancier invullen?
    @ModelField() externe_werkmaatschappij: number = null; //invulbaar door externe werkmaatschappij?
    @ModelField() wijzigmelding: string | null = null;
    //related functions
    @ModelRelation({model: PlanitemFunction}) planitem_function: PlanitemFunction[] = [];

    //personel
    @ModelRelation({model: PersoneelPlanitem}) personeel_planitem: PersoneelPlanitem[] = []; //we map op personeel_planitem, even though the backend name is personeelvast_planitem. This will be fixed because the table name is used

    @ModelRelation({model: Planitemlog}) planitemlog: Planitemlog[] = []; //de log die erbij hoort

    seriesItems: Planitem[] = null; //item in same series, after checkSeries
    private seriesItemsMetPPI: boolean = false; //true als de vorige keer met PPI opgehaald
    functionlabel: string = null; //wordt gezet in setFunctionlabel, als nodig
    private functielookup: hash<Functie> = null; //zie setFunctielookup: voor functienamen in teksten
    private kritiekLeverancier: number | null = null; //zie setKritiekLeverancierfilter
    overlapcache: Map<Number, Map<Number, boolean>> = new Map(); //map per overlapminuten, en dan true / false per planitem-id. Cachet dus overlap, gegeven datplanitems niet veranderen
    personeeloverlap: PersoneelPlanitem[] = []; //zie checkPersoneeloverlap

    //caching
    private _functiontag: string | false | null = null; //not yet set

    /**
     * Return a standardized tag for the functiondescription, so we can add items to the right block
     * Returns false if there are no connected functions
     * We cache it on the fly in a private property
     */
    get functiontag(): string | false {
        if (this._functiontag !== null) {
            return this._functiontag; //cached
        }
        let mf = this.mainFunction;
        if (!mf) {
            return this._functiontag = false;
        }
        let extras = this.extraFunctions;
        extras.sort((a, b) => a - b);
        extras.unshift(mf);
        this._functiontag = extras.join('/'); //just make it an unique standardised form
        //alias?
        if (this.functionalias) {
            this._functiontag += `-${this.functionalias}`;
        }
        return this._functiontag;
    }

    get personeelids(): number[] {
        return this.personeel_planitem.map(ppi => ppi.personeelvast_id);
    }

    /**
     * Return all personeel with this item. Only valid after setPersoneel
     */
    get personeel(): Personeel[] {
        //check op niet bestaand personeel
        return this.ingevuldePPI.map(ppi => ppi.personeel);
    }

    /**
     * Helper voor de situatie dat niet alle ppi er is of geen personeel heeft ingelezen. We geven alleen de ppi's terug waar een personeel-record aan hangt
     * @private
     */
    private get ingevuldePPI(): PersoneelPlanitem[] {
        return this.personeel_planitem?.filter(ppi => !!ppi.personeel) || [];
    }

    get startdatetime(): string {
        return `${this.startdate} ${this.starttime}`;
    }

    get nlStartdag(): string {
        const dt = DateTime.fromSQL(this.startdate);
        if (!dt.isValid) {
            return '?';
        }
        return dt.toLocaleString({weekday: 'short'}, {locale: 'nl-NL'});
    }

    /**
     * Geeft de startdatum terug in Nederlands kort formaat (dd-MM-yyyy)
     */
    get nlStartdate(): string {
        const dt = DateTime.fromSQL(this.startdate);
        if (!dt.isValid) {
            return '?';
        }
        return dt.toLocaleString({day: '2-digit', month: '2-digit', year: 'numeric'}, {locale: 'nl-NL'});
    }

    get enddatetime(): string {
        return `${this.endtime > this.starttime ? this.startdate : DateTime.fromSQL(this.startdate).plus({days: 1}).toISODate()} ${this.endtime}`;
    }

    /**
     * Virtual property from owner: the connected project
     */
    get project(): Project | null {
        return (<Project>this._owner) ?? null;
    }

    set project(project: Project | null) {
        this._owner = project;
    }

    /**
     * Is this for a combined function (main function + extras)
     */
    get isCombined(): boolean {
        return this.planitem_function.length > 1;
    }

    /**
     * Geef alle functie-ids terug
     */
    get functions(): number[] {
        if (!this.planitem_function) {
            return [];
        }
        return this.planitem_function.map(pf => pf.functie_id);
    }

    /**
     * Return the main function requested for this item, or false if it's not initialized
     */
    get mainFunction(): number | false {
        if (!this.planitem_function) {
            return false;
        }
        switch (this.planitem_function.length) {
            case 0:
                return false;
            case 1:
                return this.planitem_function[0].functie_id; //whatever
            default:
                return (this.planitem_function.find(pf => pf.main) ?? this.planitem_function[0]).functie_id;
        }
    }

    /**
     * Make sure the given function is set as main function (and when needed added)
     * the old main function will NOT be added as an extra function, but be removed
     * @param f_id
     */
    set mainFunction(f_id: number | false) {
        //cleanup the old one
        this.planitem_function = this.planitem_function.filter(f => !f.main);
        //and flag or add the new one
        for (let f of this.planitem_function) {
            if (f_id && f.functie_id === f_id) {
                f.main = true; //this one is now main
                return; //done
            }
        }
        //not found, so add it
        this.planitem_function.push(
            PlanitemFunction.createNew(this, {
                functie_id: f_id,
                main: true
            })
        );
    }

    /**
     * Return all extra functions that are not main
     */
    get extraFunctions(): number[] {
        let mainf = this.mainFunction;
        if (!mainf) {
            //never mind
            return [];
        }
        return this.functions.filter(fi => fi !== mainf);
    }

    //aantal ingevuld
    get implemented(): number {
        return this.personeel_planitem?.length ?? 0;
    }

    /**
     * Geeft true als er een planitem met ook echt gekoppeld personeel is
     */

    get heeftInvulling(): boolean {
        return !!this.personeel_planitem?.some(ppi => !!ppi.personeelvast_id);
    }

    /**
     * Number of missing implementations
     */
    get implementationDegree(): number {
        return this.implemented - this.capacity;
    }

    /**
     * Call from planbord: make sure we know which personeel is going to work on this item. Needed for kritiek, so we call hergenereerKritiek
     * @param personeelLookup
     */
    setPersoneel(personeelLookup: hash<Personeel>) {
        //our personeel_planitem relation will take care of this
        for (let ppi of this.personeel_planitem || []) {
            ppi.setPersoneel(personeelLookup);
        }
        this.resetKritiek(); //make sure it is rebuild when needed
    }

    /**
     * Herbereken deel van kritiek op verzoek: bepaal of er
     * personeelsoverlap is gegeven de arbeidstijdenwet en de gegeven items
     * Haalt om efficiencyredenen niet zelf items op maar krijgt ze van
     * de aanroeper (planbord)
     * Slaat op of en waar er overlap is, en dat wordt getoond in kritiek
     * @private
     */
    checkPersoneeloverlap(checkitems: Planitem[]) {
        this.personeeloverlap = []; //weer leg
        //kan alleen als de relevante info er is
        if (this.personeel_planitem?.length && this.project && this.startdate?.length && this.starttime?.length && this.endtime?.length) {
            const pauzeminuten = 8 * 60; //8 uur pauze tussen diensten


            //We filteren er keihard doorheen, overlap gaat snel genoeg
            //we doen eerst items met overlap, daarna pas personeel
            let items = checkitems.filter(item => item.id !== this.id && this.overlap(item, pauzeminuten));

            //overlap is als er in een overlappend item een personeelslid zit dat ook bij ons zit
            //maar: als we al personeel-info hebben en het blijkt dat de wm van het personeel niet ons wm is, dan skippen we. Dit zorgt bij inlenen van / uitlenen aan een andere wm voor correct gedrag; het project is altijd onze eigen wm (placeholder bij uitlenen), dus is het ons eigen personeel
            this.personeeloverlap = this.personeel_planitem.filter(ppi => {
                    if (ppi?.personeel?.werkmaatschappij_id !== this.project.werkmaatschappij_id) {
                        return false;
                    }
                    return items.some(item => item.personeel_planitem.some(ppi2 => {
                        return (ppi2.personeelvast_id === ppi.personeelvast_id)
                    }));
                }
            );
            this.resetKritiek(); //kritiek moet nu opnieuw
        }
    }

    /**
     * Geeft true als dit item overlapt met het andere
     * @param pi2
     * @param pauzeMinuten als er minder dan zoveel minuten pauze is, dan geldt het als overlap (Arbeidstijdenwet)
     */
    overlap(pi2: Planitem, pauzeMinuten: number = 0): boolean {
        /**
         * Overlap is:
         * begin van pi2 ligt voor eind van deze en eind van pi2 ligt na begin van deze
         */

            //caching
        let cache: Map<Number, boolean>;
        if (this.overlapcache.has(pauzeMinuten)) {
            cache = this.overlapcache.get(pauzeMinuten);
        } else {
            cache = new Map();
            this.overlapcache.set(pauzeMinuten, cache);
        }

        if (cache.has(pi2.id)) {
            return (cache.get(pi2.id)); //true of false
        }


        let startMetPauze = pauzeMinuten ? DateTime.fromSQL(this.startdatetime).minus({minutes: pauzeMinuten}).toFormat('yyyy-MM-dd HH:mm') : this.startdatetime;
        let eindMetPauze = pauzeMinuten ? DateTime.fromSQL(this.enddatetime).plus({minutes: pauzeMinuten}).toFormat('yyyy-MM-dd HH:mm') : this.enddatetime;
        let overlappend = (pi2.startdatetime < eindMetPauze) && (pi2.enddatetime > startMetPauze);
        cache.set(pi2.id, overlappend);

        return overlappend;
    }

    /**
     * Zet de leveranciers van de personeel_planitems
     * @param leverancierLookup
     */
    setLeveranciers(leverancierLookup: hash<Leverancier>) {
        for (let ppi of this.personeel_planitem) {
            ppi.setLeverancier(leverancierLookup);
        }
        this.resetKritiek(); //make sure it is rebuild when needed
    }

    /**
     * Returns true if this item has the given function (id) as extra - not main - function
     * @param f
     */
    hasExtrafunction(f: Functie | number): boolean {
        if (!this.planitem_function) {
            return false;
        }
        let searchid = f instanceof Functie ? f.id : f;
        return this.planitem_function.some(fi => (!fi.main) && fi.functie_id === searchid);
    }

    /**
     * Geeft dit item een lookup van functies (id => Functie), zodat het functienamen kan gebruiken in
     * teksten zoals de kritiek. setFunctionlabel doet dit ook; los aanroepen is nodig in schermen die
     * geen functielabel opbouwen.
     */
    setFunctielookup(functielookup: hash<Functie>) {
        if (functielookup) {
            this.functielookup = functielookup;
            this.resetKritiek(); //de teksten kunnen nu completer
        }
    }

    /**
     * Maakt onze functionlabel prop voor weergave van de functies in dit item, gestandaardidseerd. Gegeven een lookup van functies volgens id => Functie
     */
    setFunctionlabel(functielookup: hash<Functie>): string {
        this.setFunctielookup(functielookup);
        let mainf = this.mainFunction;
        if (mainf) {
            let names = [];

            if (this.isCombined) {
                //there are more functions
                names = this.extraFunctions.map(function_id => (functielookup[function_id]?.naam || '(onbekende functie)'));
                names.sort((a, b) => a.localeCompare(b));
            }
            names.unshift(functielookup[mainf]?.naam || '(onbekende functie)');
            this.functionlabel = names.join(' / ');
            if (this.functionalias) {
                this.functionlabel += ` (${this.functionalias})`;
            }
        } else {
            this.functionlabel = '?';
        }
        return this.functionlabel;
    }

    /**
     * Haal de planitemlog op voor dit item
     */
    async vulLog() {
        if (this.isNieuw()) {
            return;
        }
        //aparte call
        try {
            const log = await this._api.cachedSend('planning', 'planitemlog', {planitem: this.id}, ['planitem', 'planitemlog'], false);
            if (log?.result?.length) {
                this.planitemlog = Planitemlog.createAll(log.result, this);
            } else {
                this.planitemlog = [];
            }
        } catch {
        }
    }

    /**
     * Reset all caches, so they will be recalculated
     */
    resetCaches() {
        this._functiontag = null;
    }

    ///////////////////// Series //////////////////
    /**
     * Haal alle verbonden planitems uit de reeks, eventueel met PPI
     */
    async checkSeries(force: boolean = false, througOwner: typeof Basemodel = null, metPPI: boolean = false) {
        if (this.seriesItems && !force && ((!metPPI) || this.seriesItemsMetPPI)) {
            return; //done
        }
        this.seriesItemsMetPPI = metPPI; //alvast zetten
        if (!this.serieskey) {
            //not a series
            this.seriesItems = [];
            return;
        }
        let where = {
            serieskey: this.serieskey
        };

        this.seriesItems = await Planitem.getAll(metPPI ? [PersoneelPlanitem] : [], [[Planitem, {
            where: where,
            order: ['startdate', 'starttime', 'endtime']
        }]], null, null, througOwner);
        if ((!this.seriesItems.length) || (this.seriesItems.length === 1 && this.id && this.seriesItems[0].id === this.id)) {
            this._log.debug(`Geen verbonden items meer in reeks: afschaffen reeks`);
            this.serieskey = null;
            this.seriesItems = [];
        }
    }

    /**
     * Detach this item from the series (on save)
     */
    resetSeries() {
        this.serieskey = null;
        this.seriesItems = null;
    }

    /**
     * Verzoek om "alle" volgende items in dezelfde zet functies/aliassen te halen. De definitie van "alle" zit in de query. Voor nu: inderdaad alles
     * @param througOwner
     * @param metPPI
     */
    async volgendeGelijke(througOwner: typeof Basemodel = null, metPPI: boolean = false): Promise<Planitem[]> {
        const planitemwhere = {
            id: {$not: this.id},
            project_id: this.project_id,
            $or: [
                {
                    startdate: this.startdate,
                    starttime: {$gte: this.starttime}
                },
                {
                    startdate: {$gt: this.startdate}
                }
            ],
            functionalias: this.functionalias,
        };

        //we hebben uiteindelijk alle functies nodig bij het item. we querien daarom alle planitems met exact de juiste
        //alias en mainfunctie + niet-mainfuncties. Maar het zit in een $or, dus we weten het nog steeds niet zeker.
        //we doen dit frontend: we halen eerst de planitems op met juiste alias en mainfunction, en daarna alle andere functies, zodat we
        //de functiontag kunnen checken


        const basisitems = await Planitem.getAll([PlanitemFunction],
            [
                [Planitem, {
                    attributes: ['id','externe_werkmaatschappij'], //externe_wm is nodig, anders worden uitleen-items niet teruggegeven
                    where: planitemwhere
                }],
                //we checken vooraf alleen de mainfunction, en na ophalen de extra functies
                //PROBLEEM: we hebben ze allemaal nodig...
                [PlanitemFunction, {
                    where: {
                        functie_id: this.mainFunction,
                        main: true
                    },
                    required: true
                }
                ]
            ],
            null, null, througOwner
        );
        //nu halen we voor al deze planitems alle functies op
        return (await Planitem.getAll([PlanitemFunction, ...(metPPI ? [PersoneelPlanitem] : [])],
            [
                [Planitem, {
                    where: {
                        id: basisitems.map(pi => pi.id)
                    },
                    order: ['startdate', 'starttime', 'endtime']
                }],
            ])).filter(pi => pi.functiontag === this.functiontag);
    }

    ///////////////////////////////// Leverancierlock /////////////////////////
    /**
     * Geeft true als de leverancierlock bepaalt dat een leverancier niet mag locken, op basis van lock-setting en be-setting voor tijdsduur
     */
    get gelockt(): boolean {
        const start = DateTime.fromSQL(this.startdatetime);
        if (!start.isValid) {
            return true; //dan is hij gelockt.
        }

        const nu = DateTime.now();

        switch (this.leverancierlock) {
            case 'dicht':
                return true;
            case 'open':
                return nu >= start; //mag niet bij oud
            case 'auto':
                return nu >= this.leverancierSluitdatetime;
        }
    }

    /**
     * Geeft dit de datetime terug van het moment waarop de lock sluit als deze op auto zou staan. Checkt niet óf de lock op auto staat.
     * Geeft een invalid datetime terug als er onvoldoende gegevens zijn
     */
    get leverancierSluitdatetime(): DateTime {
        const start = DateTime.fromSQL(this.startdatetime);
        if (!start.isValid) {
            return DateTime.invalid('Onvoldoende data');
        }
        const locksetting: number = this._api.beSetting('leverancierlock_uren', 72);
        return start.minus({hours: locksetting});
    }

    /**
     * Wrapper om leverancierSluitdatetime, zie daar. Geeft een korte datumtijd-terug
     */
    get leverancierSluitmoment(): string {
        const dt = this.leverancierSluitdatetime;
        if (!dt.isValid) {
            return '?';
        }
        return dt.toLocaleString(DateTime.DATETIME_SHORT);
    }

    //////////////////////////////// KRITIEK /////////////////////////////////////
    /**
     * Levert per ingepland personeelslid de problemen met zijn kwalificatie op de dienstdatum: een
     * ontbrekende functie, een verlopen algemene certificering (raaomvereenkomst) en een verlopen certificering voor een
     * van de functies van deze dienst. Eén bron van waarheid voor de kritiek en voor de filtertool.
     *
     * Net als de bestaande functiecheck geldt dit alleen bij een factureerbaar project; wie dat wil
     * verruimen, past deze guard aan. Van personeel dat de functie helemaal mist, melden we de
     * certificering niet: dat is één probleem tegelijk.
     */
    get kwalificatieproblemen(): Kwalificatieprobleem[] {
        if (!(this.project?.factureerbaar && this.planitem_function && this.personeel_planitem)) {
            return [];
        }
        const problemen: Kwalificatieprobleem[] = [];
        for (const ppi of this.personeel_planitem) {
            const personeel = ppi.personeel;
            if (!personeel) {
                continue; //nog niets ingevuld, of geen personeelsdata
            }
            if (!(personeel.personeelvast_functie && this.planitem_function.every(pif => personeel.heeftFunctie(pif.functie_id)))) {
                this._log.debug(`Niet gekwalificeerd`, ppi);
                problemen.push({ppi: ppi, soort: 'functie'});
                continue;
            }
            if (!personeel.certificeringGeldigOp(this.startdate)) {
                problemen.push({ppi: ppi, soort: 'certificering_algemeen', einddatum: personeel.einddatum_certificering});
            }
            for (const pif of this.planitem_function) {
                if (!personeel.heeftFunctieGeldigOp(pif.functie_id, this.startdate)) {
                    problemen.push({
                        ppi: ppi,
                        soort: 'certificering_functie',
                        functie_id: pif.functie_id,
                        einddatum: personeel.personeel_functie.find(pf => pf.functie_id === pif.functie_id)?.einddatum_certificering
                    });
                }
            }
        }
        return problemen;
    }

    /**
     * Beperkt de kritiek tot wat een leverancier aangaat: alleen de regels over personeel dat hij zelf
     * op deze dienst levert. Al het andere valt weg, ook wat niet over een persoon gaat — dat de
     * planner te veel mensen op een dienst zet, is niet zijn zorg. Met null (de standaard) blijft alle
     * kritiek zichtbaar.
     * @param leverancier_id
     */
    setKritiekLeverancierfilter(leverancier_id: number | null) {
        if (this.kritiekLeverancier !== (leverancier_id || null)) {
            this.kritiekLeverancier = leverancier_id || null;
            this.resetKritiek();
        }
    }

    /**
     * We don't consider capacity to be filled a kritiek problem.
     * @protected
     */
    protected maakKritiek(): hash<string[]> {
        const kritiek: hash<string[]> = {
            capacity: [],
            functions: [],
            certificering: [], //verlopen certificering van ingepland personeel
            personeel: [],
            atw: [], //overlap qua arbeidstijdenwet
            afwezig: [], //afwezig personeel ingeroosterd
        };
        for (const regel of this.maakKritiekregels()) {
            if (this.kritiekLeverancier && regel.ppi?.leverancier_id !== this.kritiekLeverancier) {
                continue; //een leverancier krijgt alleen de kritiek over zijn eigen mensen
            }
            kritiek[regel.cat].push(regel.tekst);
        }
        return kritiek;
    }

    /**
     * Bouwt de kritiek op als losse regels, met bij elke regel de ppi waar hij over gaat. Zo kan
     * maakKritiek() de regels filteren voor wie niet alle ingeplande mensen mag zien.
     */
    private maakKritiekregels(): Kritiekregel[] {
        const regels: Kritiekregel[] = [];
        if (this.project?.factureerbaar && this.implementationDegree > 0) {
            regels.push({cat: 'capacity', tekst: 'Te veel mensen op de dienst'}); //bij niet factureerbare projecten maakt het ons niks uit
        }

        //ontbrekende functies en verlopen certificeringen komen uit dezelfde bron
        for (const probleem of this.kwalificatieproblemen) {
            const naam = probleem.ppi.personeel?.naam || 'Een ingepland personeelslid';
            switch (probleem.soort) {
                case 'functie':
                    regels.push({cat: 'functions', tekst: `${naam} is niet gekwalificeerd voor deze dienst.`, ppi: probleem.ppi});
                    break;
                case 'certificering_algemeen':
                    regels.push({
                        cat: 'certificering',
                        tekst: `De raamovereenkomst van ${naam} is verlopen op ${this.nlDatum(probleem.einddatum)}.`,
                        ppi: probleem.ppi
                    });
                    break;
                case 'certificering_functie':
                    regels.push({
                        cat: 'certificering',
                        tekst: `De certificering van ${naam} ${this.functieOmschrijving(probleem.functie_id)} is verlopen op ${this.nlDatum(probleem.einddatum)}.`,
                        ppi: probleem.ppi
                    });
                    break;
            }
        }

        //personeel uit verkeerde werkmaatschappij / heeft geen leverancier
        for (let ppi of this.personeel_planitem || []) {
            if (ppi.personeel && (![this.project?.werkmaatschappij_id, this.externe_werkmaatschappij, this.project?.externe_werkmaatschappij].includes(ppi.personeel.werkmaatschappij_id))) {
                regels.push({cat: 'personeel', tekst: `${ppi.personeel.naam || 'Een ingepland personeelslid'} komt uit een andere werkmaatschappij.`, ppi: ppi});
            }
            if (ppi.personeel && ppi.personeel.groep !== 'Personeel' && !ppi.leverancier_id) {
                regels.push({cat: 'personeel', tekst: `Bij ${ppi.personeel.naam || 'een ingepland personeelslid'} is nog geen leverancier aangegeven.`, ppi: ppi});
            }
        }

        //overlappend?
        for (let overlappend of this.personeeloverlap || []) {
            regels.push({cat: 'atw', tekst: `ATW / Overlap: ${overlappend?.personeel?.naam || 'Een personeelslid'} is al ingeroosterd.`, ppi: overlappend});
        }

        //afwezigheid
        if (this.startdate?.length) {
            for (let ppi of this.personeel_planitem || []) {
                if (ppi.personeel?.afwezigOp(this.startdate, this.starttime || '00:00', this.endtime || '00:00')) {
                    regels.push({cat: 'afwezig', tekst: `${ppi.personeel.naam} is op het geplande moment afwezig`, ppi: ppi});
                }
            }
        }

        return regels;
    }

    /**
     * Levert de aanduiding van een functie voor in een kritiektekst. Zonder functielookup kennen we
     * de naam niet en houden we het algemeen.
     * @param functie_id
     */
    private functieOmschrijving(functie_id: number): string {
        const naam = this.functielookup?.[functie_id]?.naam;
        return naam ? `voor functie ${naam}` : 'voor een van de functies van deze dienst';
    }

    /**
     * Levert een sql-datum in de Nederlandse notatie dd-mm-jjjj, of '?' als hij niet te lezen is.
     * @param datum
     */
    private nlDatum(datum: string): string {
        const dt = DateTime.fromSQL(datum || '');
        return dt.isValid ? dt.toFormat('dd-MM-yyyy') : '?';
    }
}
